import { Fragment, useContext, useEffect, useRef, useState } from "react";
import {
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "@context/AuthContext";
import { AppContext } from "@context/AppContext";
import { fireConfetti } from "@utils/confetti";
import { IPlan, PLAN, getPlanFromPayment } from "@utils/payment";
import { INTEGRATION_MAKE_ARTICLE_URL } from "@utils/constant";
import { updatePlan } from "@apis/plan";
import { Trans, useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose?: () => void;
};

function UpdatePlanModal({ open = false, onClose = () => {} }: Props) {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  const userAuth = useContext(AuthContext);
  const cancelButtonRef = useRef(null);
  const [category, setCategory] = useState("annually");
  const [toPlan, setToPlan] = useState<IPlan | null>(null);
  const [isConfirmToUpdateModalShowing, setIsConfirmToUpdateModalShowing] =
    useState(false);
  const navigate = useNavigate();
  const userPlan = getPlanFromPayment(user?.payment);
  const billingLabel =
    category === "monthly"
      ? t("plan_billing_monthly")
      : t("plan_billing_annually");

  useEffect(() => {
    function onPaddleEvent(event: CustomEvent) {
      const data = event.detail;
      if (data.event === "Checkout.Complete") {
        fireConfetti();
        toast.success("Thanks for your purchase! Refreshing in seconds...");
        setTimeout(() => {
          navigate(0);
        }, 1500);
      } else if (data.event === "Checkout.Close") {
        toast.info("Checkout has been cancelled.");
      }
    }

    window.addEventListener("paddleEvent", onPaddleEvent);
    return () => {
      window.removeEventListener("paddleEvent", onPaddleEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doUpdatePlan(planId: string) {
    try {
      await updatePlan(planId);
      toast.success(
        "Your plan has been updated successfully! Refreshing in seconds..."
      );
      setTimeout(() => {
        navigate(0);
      }, 1500);
    } catch (error) {
      toast.error(
        "Something went wrong when updating plan. Please try again later."
      );
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:align-middle">
              <div className="">
                <div className="bg-white px-6 pt-8 pb-6 sm:p-8">
                  <div>
                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                      <div className="container mx-auto px-5 py-6">
                        <div className="mb-8 flex w-full flex-col items-center">
                          <div className="inline-flex rounded-lg bg-gray-100 p-1">
                            <button
                              className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                                category === "monthly"
                                  ? "bg-white text-gray-900 shadow-sm"
                                  : "text-gray-500 hover:text-gray-900"
                              }`}
                              onClick={() => setCategory("monthly")}
                            >
                              {t("plan_billing_monthly")}
                            </button>
                            <button
                              className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                                category === "annually"
                                  ? "bg-white text-gray-900 shadow-sm"
                                  : "text-gray-500 hover:text-gray-900"
                              }`}
                              onClick={() => setCategory("annually")}
                            >
                              {t("plan_billing_annually")}
                              <span className="ml-1 text-xs text-green-600 font-semibold">
                                {t("plan_save_percent", { percent: 20 })}
                              </span>
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                          {Object.keys(PLAN).map((plan, index) => {
                            const isPopular = PLAN[plan].POPULAR;
                            const currentPlanObject = PLAN[plan];
                            const selectedPlanId =
                              category === "monthly"
                                ? currentPlanObject.MONTHLY_PRODUCT_ID!
                                : currentPlanObject.YEARLY_PRODUCT_ID!;

                            const isUserCurrentPlan =
                              selectedPlanId ===
                              user?.payment?.subscription_plan_id;
                            const isFreePlan = plan === PLAN.FREE.TITLE;
                            const isClickable =
                              !isUserCurrentPlan && !isFreePlan;

                            return (
                              <div
                                className="relative"
                                key={index}
                              >
                                <div
                                  className={`relative flex h-full flex-col rounded-xl p-8 transition-all duration-200 hover:shadow-lg ${
                                    isPopular
                                      ? "border-2 border-green-500 bg-green-50/30"
                                      : isUserCurrentPlan
                                      ? "border-2 border-blue-500 bg-blue-50/30"
                                      : "border border-gray-200 bg-white hover:border-gray-300"
                                  }`}
                                >
                                  {isPopular && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                      <span className="inline-flex items-center rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white">
                                        {t("plan_most_popular")}
                                      </span>
                                    </div>
                                  )}
                                  {isUserCurrentPlan && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                      <span className="inline-flex items-center rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white">
                                        {t("plan_current")}
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-center">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                                      {PLAN[plan].TITLE}
                                    </h3>
                                    <div className="mb-6">
                                      {category === "monthly" && (
                                        <div className="flex items-baseline justify-center">
                                          <span className="text-4xl font-bold text-gray-900">${PLAN[plan].PRICE_MONTHLY}</span>
                                          <span className="ml-1 text-lg text-gray-500">
                                            {t("plan_per_month")}
                                          </span>
                                        </div>
                                      )}
                                      {category === "annually" && (
                                        <div className="flex items-baseline justify-center">
                                          <span className="text-4xl font-bold text-gray-900">${PLAN[plan].PRICE_YEARLY}</span>
                                          <span className="ml-1 text-lg text-gray-500">
                                            {t("plan_per_month")}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex-1 space-y-3">
                                    <div className="flex items-center text-sm text-gray-600">
                                      <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span className="font-semibold text-gray-900">{PLAN[plan].TEMPLATE_LIMIT}</span>
                                      <span className="ml-1">{t("plan_templates")}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span className="font-semibold text-gray-900">{PLAN[plan].RENDERING_LIMIT}</span>
                                      <span className="ml-1">
                                        {t("plan_renderings_per_month")}
                                      </span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span>{t("plan_rest_api_integration")}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      <span>{t("plan_easy_sdks")}</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      <a
                                        href={INTEGRATION_MAKE_ARTICLE_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="hover:text-green-600 transition-colors underline"
                                      >
                                        {t("plan_nocode_integration")}
                                      </a>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      {plan === PLAN.FREE.TITLE ? (
                                        <XMarkIcon className="mr-3 h-4 w-4 text-gray-400 flex-shrink-0" />
                                      ) : (
                                        <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      )}
                                      <span className={plan === PLAN.FREE.TITLE ? "text-gray-400" : ""}>
                                        {t("plan_rendering_history")}
                                      </span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                      {plan === PLAN.FREE.TITLE ? (
                                        <XMarkIcon className="mr-3 h-4 w-4 text-gray-400 flex-shrink-0" />
                                      ) : (
                                        <CheckIcon className="mr-3 h-4 w-4 text-green-500 flex-shrink-0" />
                                      )}
                                      <span className={plan === PLAN.FREE.TITLE ? "text-gray-400" : ""}>
                                        {t("plan_priority_support")}
                                      </span>
                                    </div>
                                    {plan !== PLAN.FREE.TITLE && (
                                      <>
                                        <div className="flex items-center py-2">
                                          <div className="flex-grow border-t border-gray-200"></div>
                                          <span className="px-3 text-xs text-gray-400 bg-white">
                                            {t("plan_coming_soon")}
                                          </span>
                                          <div className="flex-grow border-t border-gray-200"></div>
                                        </div>
                                        <div className="flex items-center text-sm text-gray-500">
                                          <CheckIcon className="mr-3 h-4 w-4 text-gray-400 flex-shrink-0" />
                                          <span>{t("plan_template_gallery")}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className="mt-8">
                                    <button
                                      className={`w-full py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                        isUserCurrentPlan
                                          ? "bg-blue-500 text-white cursor-default"
                                          : isFreePlan
                                          ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                          : isClickable
                                          ? "bg-green-500 text-white hover:bg-green-600 shadow-sm hover:shadow"
                                          : "bg-gray-100 text-gray-500 cursor-not-allowed"
                                      }`}
                                      onClick={() => {
                                        if (!isClickable) {
                                          return;
                                        }

                                        if (userPlan === PLAN.FREE) {
                                          (window as any).Paddle.Checkout.open({
                                            product: selectedPlanId,
                                            email: userAuth?.email,
                                            passthrough: JSON.stringify({
                                              user_id: user?.id,
                                            }),
                                          });
                                        } else {
                                          setToPlan(currentPlanObject);
                                          setIsConfirmToUpdateModalShowing(true);
                                        }
                                      }}
                                      disabled={!isClickable}
                                    >
                                      {isFreePlan
                                        ? t("plan_get_started")
                                        : isUserCurrentPlan
                                        ? t("plan_current")
                                        : t("plan_upgrade_to", {
                                            plan: PLAN[plan].TITLE,
                                          })}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-8">
                  <button
                    type="button"
                    className="w-full justify-center rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
                    onClick={() => {
                      onClose();
                    }}
                    ref={cancelButtonRef}
                  >
                    {t("common_close")}
                  </button>
                </div>
              </div>
              {isConfirmToUpdateModalShowing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50">
                  <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl">
                    <div className="p-6">
                      <div className="text-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {t("plan_confirm_title")}
                        </h3>
                        <p className="text-sm text-gray-600">
                          <Trans
                            i18nKey="plan_confirm_body"
                            values={{
                              from: userPlan.TITLE,
                              to: `${toPlan?.TITLE} (${billingLabel})`,
                            }}
                            components={{
                              strong: (
                                <span className="font-semibold text-gray-900" />
                              ),
                            }}
                          />
                        </p>
                      </div>

                      <div className="rounded-lg bg-blue-50 p-4 mb-6">
                        <div className="flex">
                          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">
                              {t("plan_notice_title")}
                            </h4>
                            <ul className="text-sm text-blue-700 space-y-1">
                              <li>{t("plan_notice_item_1")}</li>
                              <li>{t("plan_notice_item_2")}</li>
                              <li>{t("plan_notice_item_3")}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex gap-3 rounded-b-xl">
                      <button
                        type="button"
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        onClick={() => setIsConfirmToUpdateModalShowing(false)}
                      >
                        {t("common_cancel")}
                      </button>
                      <button
                        type="button"
                        className="flex-1 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        onClick={() => {
                          setIsConfirmToUpdateModalShowing(false);
                          const planId = category === "monthly"
                            ? toPlan?.MONTHLY_PRODUCT_ID
                            : toPlan?.YEARLY_PRODUCT_ID;
                          if (planId) {
                            doUpdatePlan(planId);
                          }
                        }}
                      >
                        {t("plan_confirm_submit")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default UpdatePlanModal;
